import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExchangeAlt,
  FaArrowLeft,
  FaSyncAlt,
  FaSearch,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaUndo,
  FaRegClock,
  FaBan,
  FaInfoCircle,
  FaCalendarAlt,
  FaFolderOpen,
} from "react-icons/fa";
import { getReturnedCheques, getActiveCheques, updateChequeStatus } from "./chequeReturnsService";
import "./ChequeReturns.css";

const fmtCurrency = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "AED" }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return "—";
  // Safe date parsing for YYYY-MM-DD
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return d;
  }
};

export default function ChequeReturnsPage() {
  const navigate = useNavigate();

  // ── States ─────────────────────────────────────────────────
  const [bouncedCheques, setBouncedCheques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all"); // 'all' | 'receipt' | 'payment'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCheques, setActiveCheques] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSearch, setModalSearch] = useState("");
  const [modalCategory, setModalCategory] = useState("all");
  const [workingId, setWorkingId] = useState(null); // 'receipt-123'

  // ── Toast Helper ───────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load Bounced Cheques ───────────────────────────────────
  const loadBouncedCheques = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getReturnedCheques({ cheque_type: "all" });
      setBouncedCheques(data.cheques || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load cheque returns from the server.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBouncedCheques();
  }, [loadBouncedCheques]);

  // ── Load Active Cheques (for Modal) ────────────────────────
  const loadActiveCheques = async () => {
    setModalLoading(true);
    try {
      // Fetch both receipts and payments that are Pending or Cleared
      const dataPending = await getActiveCheques({ cheque_status: "Pending" });
      const dataCleared = await getActiveCheques({ cheque_status: "Cleared" });
      
      const combined = [
        ...(dataPending.cheques || []),
        ...(dataCleared.cheques || [])
      ];
      
      // Sort by cheque date
      combined.sort((a, b) => new Date(a.cheque_date) - new Date(b.cheque_date));
      setActiveCheques(combined);
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch active cheques for recording returns.", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    loadActiveCheques();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveCheques([]);
    setModalSearch("");
  };

  // ── Actions ────────────────────────────────────────────────
  const handleMarkAsReturned = async (cheque) => {
    const key = `${cheque.txn_type}-${cheque.id}`;
    setWorkingId(key);
    try {
      await updateChequeStatus(cheque.txn_type, cheque.id, "Bounced");
      showToast(`Cheque #${cheque.cheque_number} has been marked as Returned.`);
      
      // Remove from modal list locally
      setActiveCheques((prev) => prev.filter((c) => `${c.txn_type}-${c.id}` !== key));
      
      // Refresh main dashboard
      loadBouncedCheques();
    } catch (err) {
      console.error(err);
      showToast("Failed to update cheque status to Bounced.", "error");
    } finally {
      setWorkingId(null);
    }
  };

  const handleUndoReturn = async (cheque) => {
    const key = `${cheque.txn_type}-${cheque.id}`;
    setWorkingId(key);
    try {
      // Reverting to Pending is the safest accounting state for returned cheques
      await updateChequeStatus(cheque.txn_type, cheque.id, "Pending");
      showToast(`Return undone. Cheque status set to Pending.`);
      
      // Refresh main dashboard
      loadBouncedCheques();
    } catch (err) {
      console.error(err);
      showToast("Failed to reverse cheque return status.", "error");
    } finally {
      setWorkingId(null);
    }
  };

  // ── Computations ───────────────────────────────────────────
  // Calculate stats based on ALL bounced cheques fetched from server
  const stats = bouncedCheques.reduce(
    (acc, c) => {
      const amt = Number(c.amount || 0);
      acc.totalCount += 1;
      acc.totalAmount += amt;

      if (c.txn_type === "receipt") {
        acc.receiptCount += 1;
        acc.receiptAmount += amt;
      } else {
        acc.paymentCount += 1;
        acc.paymentAmount += amt;
      }
      return acc;
    },
    {
      totalCount: 0,
      totalAmount: 0,
      receiptCount: 0,
      receiptAmount: 0,
      paymentCount: 0,
      paymentAmount: 0,
    }
  );

  // Apply dashboard filters locally
  const filteredBounced = bouncedCheques.filter((c) => {
    // 1. Search Query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchNo = c.cheque_number ? c.cheque_number.toLowerCase().includes(query) : false;
      const matchParty = c.party ? c.party.toLowerCase().includes(query) : false;
      const matchVoucher = c.voucher_no ? c.voucher_no.toLowerCase().includes(query) : false;
      if (!matchNo && !matchParty && !matchVoucher) return false;
    }

    // 2. Category Filter
    if (categoryFilter !== "all" && c.txn_type !== categoryFilter) {
      return false;
    }

    // 3. Date Filters
    if (startDate && c.cheque_date && c.cheque_date < startDate) return false;
    if (endDate && c.cheque_date && c.cheque_date > endDate) return false;

    return true;
  });

  // Apply modal filters locally
  const filteredActive = activeCheques.filter((c) => {
    if (modalSearch.trim()) {
      const query = modalSearch.toLowerCase();
      const matchNo = c.cheque_number ? c.cheque_number.toLowerCase().includes(query) : false;
      const matchParty = c.party ? c.party.toLowerCase().includes(query) : false;
      const matchVoucher = c.voucher_no ? c.voucher_no.toLowerCase().includes(query) : false;
      if (!matchNo && !matchParty && !matchVoucher) return false;
    }

    if (modalCategory !== "all" && c.txn_type !== modalCategory) {
      return false;
    }

    return true;
  });

  return (
    <div className="crt-page">
      {/* Toast Alert */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 24,
            right: 24,
            padding: "12px 24px",
            borderRadius: 8,
            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
            background: toast.type === "error" ? "#fee2e2" : "#d1fae5",
            color: toast.type === "error" ? "#991b1b" : "#065f46",
            border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#6ee7b7"}`,
            zIndex: 9999,
            fontWeight: 600,
            fontSize: "0.88rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {toast.type === "error" ? <FaTimesCircle /> : <FaCheckCircle />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="crt-header">
        <div className="crt-title-group">
          <h2>
            <FaExchangeAlt /> Cheque Returns & Bounces
          </h2>
          <p>Manage receipts and payments that have been returned/bounced by the bank</p>
        </div>
        <div className="crt-header-actions">
          <button className="crt-btn crt-btn-back" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Dashboard
          </button>
          <button className="crt-btn crt-btn-secondary" onClick={loadBouncedCheques} disabled={loading}>
            <FaSyncAlt className={loading ? "fa-spin" : ""} /> Refresh
          </button>
          <button className="crt-btn crt-btn-primary" onClick={handleOpenModal}>
            <FaExclamationTriangle /> Record Return / Bounce
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="crt-summary-grid">
        <div className="crt-card total">
          <div className="crt-card-header-icon">
            <span className="crt-card-label">Total Returned</span>
            <FaExclamationTriangle className="crt-card-icon" />
          </div>
          <div className="crt-card-value">{fmtCurrency(stats.totalAmount)}</div>
          <div className="crt-card-sub">{stats.totalCount} Cheques Bounced</div>
        </div>

        <div className="crt-card receipts">
          <div className="crt-card-header-icon">
            <span className="crt-card-label">Receipt Returns (Customer)</span>
            <FaTimesCircle className="crt-card-icon" />
          </div>
          <div className="crt-card-value">{fmtCurrency(stats.receiptAmount)}</div>
          <div className="crt-card-sub">{stats.receiptCount} Customer Cheques</div>
        </div>

        <div className="crt-card payments">
          <div className="crt-card-header-icon">
            <span className="crt-card-label">Payment Returns (Supplier)</span>
            <FaUndo className="crt-card-icon" />
          </div>
          <div className="crt-card-value">{fmtCurrency(stats.paymentAmount)}</div>
          <div className="crt-card-sub">{stats.paymentCount} Supplier Cheques</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="crt-filters-panel">
        <div className="crt-filter-group search-group">
          <label>Search Cheques</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              className="crt-filter-input"
              style={{ paddingLeft: 34, width: "100%" }}
              placeholder="Search by Cheque No, Party or Voucher No..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FaSearch style={{ position: "absolute", left: 12, top: 13, color: "#94a3b8" }} />
          </div>
        </div>

        <div className="crt-filter-group">
          <label>Category</label>
          <select
            className="crt-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="receipt">Receipt (Customer)</option>
            <option value="payment">Payment (Supplier)</option>
          </select>
        </div>

        <div className="crt-filter-group">
          <label>Cheque Date From</label>
          <input
            type="date"
            className="crt-filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="crt-filter-group">
          <label>Cheque Date To</label>
          <input
            type="date"
            className="crt-filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {(searchQuery || categoryFilter !== "all" || startDate || endDate) && (
          <button
            className="crt-btn crt-btn-secondary"
            style={{ padding: "10px 14px", height: 42 }}
            onClick={() => {
              setSearchQuery("");
              setCategoryFilter("all");
              setStartDate("");
              setEndDate("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Main Table */}
      <div className="crt-table-container">
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <FaSyncAlt className="fa-spin" style={{ fontSize: "2rem", color: "#6366f1", marginBottom: 12 }} />
            <p style={{ color: "#64748b" }}>Loading returned cheques...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 60, color: "#dc2626" }}>
            <FaExclamationTriangle style={{ fontSize: "2rem", marginBottom: 12 }} />
            <p>{error}</p>
          </div>
        ) : filteredBounced.length === 0 ? (
          <div className="crt-empty-state">
            <FaFolderOpen className="crt-empty-icon" />
            <h3>No Returned Cheques Found</h3>
            <p>Adjust your search filters or record a new cheque return above.</p>
          </div>
        ) : (
          <table className="crt-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Voucher No</th>
                <th>Cheque No</th>
                <th>Cheque Date</th>
                <th>Party Name</th>
                <th>Amount</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBounced.map((c) => {
                const key = `${c.txn_type}-${c.id}`;
                const isWorking = workingId === key;
                return (
                  <tr key={key}>
                    <td>
                      <span className={`crt-badge crt-badge-${c.txn_type}`}>
                        {c.txn_type === "receipt" ? "Receipt" : "Payment"}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.voucher_no}</td>
                    <td>{c.cheque_number || "—"}</td>
                    <td>{fmtDate(c.cheque_date)}</td>
                    <td>{c.party}</td>
                    <td style={{ fontWeight: 700, color: c.txn_type === "receipt" ? "#0ca678" : "#f03e3e" }}>
                      {fmtCurrency(c.amount)}
                    </td>
                    <td>
                      <span className="crt-badge crt-status-badge">Returned</span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="crt-action-btn undo"
                        disabled={isWorking}
                        onClick={() => handleUndoReturn(c)}
                        title="Revert status to Pending"
                      >
                        <FaUndo /> {isWorking ? "Reverting..." : "Undo Return"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Dialog */}
      {isModalOpen && (
        <div className="crt-modal-overlay" onClick={handleCloseModal}>
          <div className="crt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crt-modal-header">
              <h3>
                <FaExclamationTriangle style={{ color: "#ef4444" }} /> Record Cheque Return / Bounce
              </h3>
              <button className="crt-modal-close" onClick={handleCloseModal}>
                <FaBan style={{ fontSize: "1.1rem" }} />
              </button>
            </div>
            
            <div className="crt-modal-body">
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b" }}>
                Select an active (Pending/Cleared) cheque from the list below to mark it as Bounced (Returned). This updates the cheque status and updates the bank ledger.
              </p>
              
              <div className="crt-modal-search-row">
                <div style={{ position: "relative", flex: 1 }}>
                  <input
                    type="text"
                    className="crt-filter-input"
                    style={{ paddingLeft: 34, width: "100%" }}
                    placeholder="Search by Cheque No, Party..."
                    value={modalSearch}
                    onChange={(e) => setModalSearch(e.target.value)}
                  />
                  <FaSearch style={{ position: "absolute", left: 12, top: 13, color: "#94a3b8" }} />
                </div>
                
                <select
                  className="crt-filter-select"
                  style={{ width: 180 }}
                  value={modalCategory}
                  onChange={(e) => setModalCategory(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="receipt">Receipts (Customers)</option>
                  <option value="payment">Payments (Suppliers)</option>
                </select>
              </div>

              <div className="crt-modal-table-container">
                {modalLoading ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    <FaSyncAlt className="fa-spin" style={{ fontSize: "1.5rem", color: "#6366f1", marginBottom: 8 }} />
                    <p style={{ color: "#64748b" }}>Loading cheques...</p>
                  </div>
                ) : filteredActive.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
                    <FaFolderOpen style={{ fontSize: "2rem", marginBottom: 8 }} />
                    <p>No active cheques found matching filters.</p>
                  </div>
                ) : (
                  <table className="crt-modal-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Voucher No</th>
                        <th>Cheque No</th>
                        <th>Cheque Date</th>
                        <th>Party</th>
                        <th>Amount</th>
                        <th style={{ textAlign: "right" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActive.map((c) => {
                        const key = `${c.txn_type}-${c.id}`;
                        const isWorking = workingId === key;
                        return (
                          <tr key={key}>
                            <td>
                              <span className={`crt-badge crt-badge-${c.txn_type}`}>
                                {c.txn_type === "receipt" ? "Receipt" : "Payment"}
                              </span>
                            </td>
                            <td style={{ fontWeight: 600 }}>{c.voucher_no}</td>
                            <td>{c.cheque_number || "—"}</td>
                            <td>{fmtDate(c.cheque_date)}</td>
                            <td>{c.party}</td>
                            <td style={{ fontWeight: 700 }}>{fmtCurrency(c.amount)}</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                className="crt-action-btn"
                                disabled={isWorking || workingId !== null}
                                onClick={() => handleMarkAsReturned(c)}
                                style={{ background: "#ffe3e3", color: "#e03131", borderRadius: 6 }}
                              >
                                {isWorking ? "Updating..." : "Bounce Return"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            <div className="crt-modal-footer">
              <button className="crt-btn crt-btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
